import { useReactiveVar } from '@apollo/client';

import { userStateVar } from 'core/state/userState';
import { UserRole } from 'types';

import { LEAD_ROLES } from '../Crm/useLeadPermissions';
import { DOFIN_MANAGER_ROLES } from '../Dofinansowanie/useDofinPermissions';

/**
 * ТЗ 0081 — кто работает с SMS. Зеркало `MANAGEMENT_ROLES` бэкенда
 * (`students/schema/sms.py`, `outreach/schema.py`): журнал, рассылка по
 * группе, повтор и отмена, чтение настроек и лесенок.
 *
 * Меню, маршрут и палитра берут список ТОЛЬКО отсюда — три места не имеют
 * права разойтись. Скрытие кнопки — UX, а не защита: авторитет остаётся за
 * бэкендом. Но гейт нужен и чтобы НЕ слать запрос с чужой роли: отказ
 * рисовал бы ошибку поверх постороннего экрана (методист на карточке).
 */
export const SMS_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.HRMANAGER,
  UserRole.FINANCIER,
];

/**
 * Ручная SMS ОДНОМУ студенту (решение владельца 2026-09-22): HR со студентами
 * не переписывается — бэкенд убрал его из ролей `studentSms`,
 * `previewStudentSms` и `sendStudentSms`. Журнал, рассылка по группе и SMS
 * преподавателю у HR остаются.
 */
export const STUDENT_SMS_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.FINANCIER,
];

/**
 * SMS преподавателю — зеркало `teachers/schema/sms.py` (те же роли, что у
 * карточки преподавателя и «Написать письмо»). FinanceManager пишет
 * студенту, но не преподавателю.
 */
export const TEACHER_SMS_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.HRMANAGER,
];

/**
 * Настройки канала, проверка подключения, тестовая SMS — только Admin.
 * Шаблоны, лесенки, хвост отписки и адреса ссылок правят ещё и продавцы, а
 * тексты и лесенку оплаты — и финансы (ТЗ 0084). Это говорит сервер
 * (`canEditMessageTemplates`, `ReminderPlanType.canEdit`, `canEditTexts`,
 * `linkAddresses.canEdit`), а не роли здесь.
 */
const SMS_ADMIN_ROLES: UserRole[] = [UserRole.ADMIN];

export interface ISmsPermissions {
  /** Раздел «SMS», журнал, рассылка по группе. */
  canUse: boolean;
  /** Ручная SMS студенту: его карточка и вкладка SMS коммуникатора. */
  canSendStudent: boolean;
  /** Ручная SMS преподавателю: вкладка «Общение» его карточки. */
  canSendTeacher: boolean;
  /** Настройки канала, подключение, тестовая SMS. */
  isAdmin: boolean;
}

const hasAny = (roles: UserRole[], allowed: UserRole[]): boolean =>
  roles.some((role) => allowed.includes(role));

/** Роли из состояния пользователя без React — для чистых проверок. */
export const smsPermissionsFor = (roles: UserRole[]): ISmsPermissions => ({
  canUse: hasAny(roles, SMS_ROLES),
  canSendStudent: hasAny(roles, STUDENT_SMS_ROLES),
  canSendTeacher: hasAny(roles, SMS_ROLES) && hasAny(roles, TEACHER_SMS_ROLES),
  isAdmin: hasAny(roles, SMS_ADMIN_ROLES),
});

/**
 * Можно ли роли ПОВТОРИТЬ строку журнала этой темы. Повтор — та же отправка:
 * у SMS преподавателю свой набор ролей, и кнопка, которую сервер всё равно
 * отвергнет, — тупик. Незнакомая тема (её завёл бэкенд позже) судится общим
 * правом раздела: прятать действие из-за незнакомого кода нельзя.
 */
export const canRetrySmsTopicFor = (
  roles: UserRole[],
  topic: string | null | undefined,
): boolean => {
  const permissions = smsPermissionsFor(roles);
  if (topic === 'teacher_sms') return permissions.canSendTeacher;
  if (topic === 'student_sms') return permissions.canSendStudent;
  // ТЗ 0084: лиду пишет любой продавец, кандидату — роли дофинансирования
  // (`_may_resend` бэкенда).
  if (['lead_sms', 'lead_reminder', 'lead_dofin'].includes(topic || '')) {
    return hasAny(roles, LEAD_ROLES);
  }
  if (topic === 'dofin_candidate_sms') {
    return hasAny(roles, DOFIN_MANAGER_ROLES);
  }
  return permissions.canUse;
};

export const useSmsPermissions = (): ISmsPermissions => {
  const userState = useReactiveVar(userStateVar);
  const roles = (userState.me?.roles ?? []) as UserRole[];
  return smsPermissionsFor(roles);
};

/** Предикат «можно ли повторить строку этой темы» для текущей роли. */
export const useCanRetrySmsTopic = (): ((
  topic: string | null | undefined,
) => boolean) => {
  const userState = useReactiveVar(userStateVar);
  const roles = (userState.me?.roles ?? []) as UserRole[];
  return (topic) => canRetrySmsTopicFor(roles, topic);
};
